import { useState } from 'react';
import { addToWallet, deviceWallets, type WalletKind } from '../lib/wallet.js';
import { openExternal } from '../lib/open.js';
import { useStore } from '../lib/store.js';

/*
 * The official "Add to Apple Wallet" and "Add to Google Wallet" badges, used as Apple's and Google's guidelines ask: their
 * own artwork, unaltered, at a readable size, on a button that does nothing but add the pass.
 *  - Google's badge (en-GB) is in public/badges, from Google's brand assets (developers.google.com/wallet, "Add to Google Wallet").
 *  - Apple's badge file comes from developer.apple.com/wallet/add-to-apple-wallet-guidelines ("Download badge files", behind
 *    Apple's licence agreement, which only the account holder can accept): saved as public/badges/add-to-apple-wallet-en-gb.svg
 *    (TODO-SAIF). Until that file exists, a drawn stand-in with the same words shows, so the button never breaks.
 */
const BADGE = { apple: '/badges/add-to-apple-wallet-en-gb.svg', google: '/badges/add-to-google-wallet-en-gb.svg' } as const;
const APPLE_STAND_IN = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><rect x="2" y="4" width="20" height="16" rx="3" fill="#fff"/><path d="M2 8.5h20v3H2z" fill="#ffb400"/><path d="M2 10.5h20v3H2z" fill="#3cba54"/><path d="M2 12.5h20v3H2z" fill="#1e88e5"/><rect x="2" y="14.5" width="20" height="5.5" rx="2" fill="#e53935"/></svg>';

/** The server can issue at least one kind of pass right now (the switch on, and a certificate or issuer set up). */
export function walletsIssued(s: ReturnType<typeof useStore>): boolean {
  return s.config?.features?.wallet === true && (!!s.config.wallet?.apple || !!s.config.wallet?.google);
}

/** The wallets this device has AND the server can issue now (docs/23 §2). Nothing when the Wallet pass is switched off. */
export function walletsHere(s: ReturnType<typeof useStore>, server?: { apple: boolean; google: boolean } | null): WalletKind[] {
  if (s.config?.features?.wallet !== true) return [];
  const can = server ?? s.config?.wallet ?? { apple: false, google: false };
  return deviceWallets().filter((k) => can[k]);
}

/**
 * "Add to Apple Wallet" / "Add to Google Wallet": only the one(s) this device can use, and that the server can issue.
 * Signed in, a tap asks for the pass; with `urls` (Get my card back, docs/23 §3.7) the links are already in hand.
 */
export function WalletButtons({ server, onDevice, last4, urls }: { server?: { apple: boolean; google: boolean } | null; onDevice?: { apple: boolean; google: boolean } | null; last4?: string | null; urls?: { apple: string | null; google: string | null } }) {
  const s = useStore();
  const [busy, setBusy] = useState<WalletKind | null>(null);
  const kinds = walletsHere(s, urls ? { apple: !!urls.apple, google: !!urls.google } : server);
  if (!kinds.length) return null;
  const go = async (k: WalletKind) => {
    const url = urls?.[k];
    if (url) return void openExternal(url);
    setBusy(k);
    const err = await addToWallet(k);
    setBusy(null);
    if (err) s.toast(err);
  };
  return (
    <div className="walletbtns">
      {kinds.map((k) => onDevice?.[k]
        ? <span key={k} className="small muted walletin">Your card{last4 ? ` ending ${last4}` : ''} is in {k === 'apple' ? 'Apple' : 'Google'} Wallet</span>
        : (
          <BadgeButton key={k} kind={k} busy={busy === k} disabled={busy !== null} onClick={() => go(k)} />
        ))}
    </div>
  );
}

/** One badge: the official artwork as an image; Apple's falls back to the drawn stand-in until its file is added. */
function BadgeButton({ kind, busy, disabled, onClick }: { kind: WalletKind; busy: boolean; disabled: boolean; onClick: () => void }) {
  const [missing, setMissing] = useState(false);
  const label = kind === 'apple' ? 'Add to Apple Wallet' : 'Add to Google Wallet';
  if (missing) {
    return (
      <button type="button" className={`walletbtn standin ${kind}`} disabled={disabled} onClick={onClick} aria-label={label}>
        <span className="wicon" dangerouslySetInnerHTML={{ __html: APPLE_STAND_IN }} /><span>{busy ? 'One moment…' : label}</span>
      </button>
    );
  }
  return (
    <button type="button" className={`walletbtn ${kind}`} disabled={disabled} onClick={onClick} aria-label={busy ? 'One moment…' : label} aria-busy={busy || undefined}>
      <img src={BADGE[kind]} alt="" draggable={false} onError={() => setMissing(true)} />
    </button>
  );
}
